import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks.ts";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  registerCompleteUser,
  signOutUser,
} from "../../../stores/thunks/userAuthThunk.tsx";
import { UserCompleteRegisterBodyType } from "../../../stores/types/userAuthTypes.tsx";
import Logo from "/images/logo.svg";
import { passwordRequirements } from "../../../helpers/functions/getPasswordRequirements.ts";
import {
  RevButton,
  RevInput,
  RevPasswordStrengthMeter,
  RevSelect,
  RevSpinner,
} from "@revmatics/catalyst";
import { jobTitleOptions } from "../signUp/data/signUpData.ts";
import { useLocation, useNavigate } from "react-router";
import { NotFoundPage } from "../../notFound/NotFoundPage.tsx";
import { isPasswordValid } from "../../../helpers/functions/hasValidHelpers.ts";

type UserCompleteRegisterFormData = UserCompleteRegisterBodyType;

type Props = {
  logout?: boolean;
};

export const UserCompleteRegisterPage = ({ logout = false }: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const userAuth = useAppSelector((state) => state.userAuth.token);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    control,
  } = useForm<UserCompleteRegisterFormData>({
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (logout) {
      dispatch(signOutUser());
    }
  }, [dispatch, logout]);

  const inviteToken = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("invite_token");
    setIsLoading(false);
    return token;
  }, [location.search]);

  const onFormSubmit: SubmitHandler<UserCompleteRegisterFormData> = (data) => {
    setIsSubmitting(true);
    if (inviteToken) {
      dispatch(
        registerCompleteUser({
          body: { ...data, invite_token: inviteToken },
          callback: () => {
            setIsSubmitting(false);
            navigate("/signin");
          },
        }),
      );
    }
  };

  if (isLoading || userAuth) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <RevSpinner size="large" color="primary" centered />
      </div>
    );
  }

  if (!isLoading && inviteToken && !userAuth) {
    return (
      <div className="flex flex-col items-center justify-center w-full">
        <form
          onSubmit={handleSubmit(onFormSubmit)}
          className="w-full space-y-6"
          noValidate
        >
          <div className="mb-4 flex justify-start">
            <img src={Logo} alt="Company Logo" className="h-[35px] w-auto" />
          </div>
          <h3 className="mb-2">Create Account</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-1/2">
              <RevInput
                name="first_name"
                label="First Name"
                type="text"
                size="md"
                required
                register={register("first_name", {
                  required: "First name is required.",
                })}
                error={errors.first_name}
                enableClearButton={true}
                onClear={() =>
                  setValue("first_name", "", { shouldValidate: false })
                }
              />
            </div>
            <div className="w-full sm:w-1/2">
              <RevInput
                name="last_name"
                label="Last Name"
                type="text"
                size="md"
                required
                register={register("last_name", {
                  required: "Last name is required.",
                })}
                error={errors.last_name}
                enableClearButton={true}
                onClear={() =>
                  setValue("last_name", "", { shouldValidate: false })
                }
              />
            </div>
          </div>
          <RevSelect
            name="job_title"
            label="Job title"
            options={jobTitleOptions}
            control={control}
            required
            error={errors.job_title}
            size="md"
            placeholder="Select"
          />
          <RevInput
            name="password"
            label="Password"
            type="password"
            placeholder="Set password"
            size="md"
            required
            register={register("password", {
              required: "Password is required.",
              validate: (value) => isPasswordValid(value),
            })}
            error={errors.password}
            enableClearButton={true}
            onClear={() => setValue("password", "", { shouldValidate: true })}
          />
          <RevPasswordStrengthMeter
            password={watch("password", "")}
            requirements={passwordRequirements}
          />
          <RevButton
            type="submit"
            variant="primary"
            text="Create Account"
            className="w-full mt-4"
            isLoading={isSubmitting}
            displayAs="button"
          />
        </form>
      </div>
    );
  }

  return <NotFoundPage />;
};
